import { useMemo } from "react";
import { ChevronRight, Plus, RefreshCw, Sparkles, TriangleAlert } from "lucide-react";
import { PaneHeader } from "@features/pane-manager/PaneHeader";
import { PaneFooter } from "@features/pane-manager/PaneFooter";
import { usePaneManager } from "@features/pane-manager/store";
import { useScrollMemory } from "@features/pane-manager/useScrollMemory";
import { refreshDeck, selectSlide, useDeck, REQUIRED_POWERPOINT_API } from "@features/office";
import {
    createAssignmentId,
    assignmentInertReason,
    selectAssignmentsForSlide,
    selectUnboundAssignments,
    useAssignments,
    useSlides,
} from "@entities/generation-setting";

/**
 * 画面A。生成の設定を俯瞰する。
 *
 * スライドごとに段落を空け、その枚を名指している生成担当をぶら下げる。枚をタップすると
 * 画面B、担当をタップすると画面C へ移る。移動のたびに PowerPoint 側もその枚へ飛ばす。
 *
 * 設計: [[2026-08-26 設計ノート アドイン 生成設定UIの再構成]]。
 */
export function GenerationSettingPan() {
    const deck = useDeck();
    const outline = deck.slides;
    const loading = !deck.loaded;
    // 走査に失敗しただけで、ファイルの中身は無事である。空のファイルと同じ顔をさせない。
    const failed = deck.failed;
    const openPane = usePaneManager((s) => s.openPane);
    // 戻ってきたら出ていった行へ帰す（[[2026-09-04 設計ノート アドイン 戻ったときの表示位置]]）。
    const scroll = useScrollMemory();
    const slides = useSlides();
    const assignments = useAssignments();

    const unset = useMemo(
        () =>
            outline.filter((s) => {
                const setting = slides[s.uid];
                return setting?.aim === undefined || setting.aim.trim().length === 0;
            }).length,
        [outline, slides]
    );

    /**
     * どの枚にも属さない担当。一覧が枚を起点なので、ここに出さないと開けなくなる。
     *
     * **走査に失敗したときは判定しない。** 材料（枚と枠の一覧）がゼロなので、どの担当も
     * 「属さない」と出てしまい、設定が丸ごと壊れたように見える。
     */
    const unbound = useMemo(
        () =>
            failed
                ? []
                : selectUnboundAssignments(
                      assignments,
                      outline.map((s) => ({
                          uid: s.uid,
                          elementUids: s.shapes.map((sh) => sh.uid),
                      }))
                  ),
        [assignments, outline, failed]
    );

    /** 枚へ移る。戻ってきたときこの行へ帰れるよう、出所を控えてから開く。 */
    function openSlide(slideUid: string, officeSlideId: string): void {
        scroll.leaveFrom(slideUid);
        void selectSlide(officeSlideId);
        openPane("slide");
    }

    /** 担当へ移る。同じく出所を控えてから開く。 */
    function openAssignment(id: string): void {
        scroll.leaveFrom(id);
        openPane("assignment", id);
    }

    return (
        <>
            <PaneHeader title="生成設定" />
            <div ref={scroll.ref} className="flex-1 overflow-y-auto py-2">
                {loading && (
                    <p className="px-4 py-10 text-center text-sm text-text-muted">読み込み中…</p>
                )}
                {/* 識別子を書けなかった＝読み取り専用で開いている見込み。**一覧は読めているので
                    出す**（以前は書き込みの失敗で読めた紙面まで捨てていた・記録 25）。設定を
                    書いても鍵がファイルへ残らないので、その旨を先に断る。 */}
                {!loading && !failed && deck.uidWriteFailed && (
                    <p className="mx-4 mb-2 rounded-md border border-border-subtle bg-surface px-3 py-2 text-xs text-text-secondary">
                        識別子を保存できませんでした。読み取り専用で開いていませんか。
                        <br />
                        このままでも設定は見られますが、保存しても次に開いたとき残りません。
                    </p>
                )}
                {/* 重複していた識別子を振り直した＝複製された枚・枠がある。複製側は白紙で始まる
                    のが設計だが、**複製を元より前へ動かしていた場合は元の側が白紙になる**
                    （どちらが元かはファイルから見分けられない・記録 51）。起きたことだけを伝える。 */}
                {!loading && !failed && deck.reissued > 0 && (
                    <p className="mx-4 mb-2 rounded-md border border-border-subtle bg-surface px-3 py-2 text-xs text-text-secondary">
                        複製された枚・枠が {deck.reissued} 件あり、設定を引き継いでいません。
                        <br />
                        複製を元より前へ動かしていた場合、元の側の設定が複製側へ移っている可能性があります。
                    </p>
                )}
                {!loading && failed && (
                    <p className="px-4 py-10 text-center text-sm text-text-muted">
                        紙面を読み取れませんでした。
                        <br />
                        PowerPoint でスライドを選ぶと読み直します。
                        {/* 版が足りない環境でも警告なく読み込まれるので、原因を言い添える
                            （マニフェストに必要な版の宣言が無い・記録 34）。 */}
                        {deck.officeTooOld && (
                            <>
                                <br />
                                お使いの PowerPoint が古い可能性があります（PowerPointApi{" "}
                                {REQUIRED_POWERPOINT_API} 以上が必要です）。
                            </>
                        )}
                    </p>
                )}
                {!loading && !failed && outline.length === 0 && (
                    <p className="px-4 py-10 text-center text-sm text-text-muted">
                        スライドがありません
                    </p>
                )}
                {outline.map((slide) => {
                    const setting = slides[slide.uid];
                    const elementUids = slide.shapes.map((s) => s.uid);
                    const ids = selectAssignmentsForSlide(assignments, slide.uid, elementUids);
                    const noAim = setting?.aim === undefined || setting.aim.trim().length === 0;

                    return (
                        <div
                            key={slide.uid}
                            data-anchor={slide.uid}
                            className="mb-2.5 border-y border-text-primary/6 bg-surface"
                        >
                            <button
                                type="button"
                                className="flex w-full cursor-pointer items-center gap-2.5 px-4 py-2 text-left"
                                onClick={() => openSlide(slide.uid, slide.officeSlideId)}
                            >
                                <span className="w-6 shrink-0 text-xs text-text-muted">
                                    {slide.index}
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-[13px]">
                                        {setting?.name ?? "名前なし"}
                                    </span>
                                    {noAim && (
                                        <span className="block text-[11px] text-warning">
                                            ねらいなし
                                        </span>
                                    )}
                                </span>
                                <ChevronRight size={16} className="shrink-0 text-text-muted" />
                            </button>

                            {ids.length === 0 ? (
                                <div className="flex items-center gap-2 border-t border-text-primary/5 bg-warning/10 py-1.5 pl-12 pr-4">
                                    <span className="min-w-0 flex-1 text-xs text-warning">
                                        生成担当なし
                                    </span>
                                    <button
                                        type="button"
                                        className="cursor-pointer rounded border border-border-subtle bg-surface px-2 text-[11px] text-text-secondary"
                                        onClick={() => {
                                            void selectSlide(slide.officeSlideId);
                                            openAssignment(createAssignmentId());
                                        }}
                                    >
                                        足す
                                    </button>
                                </div>
                            ) : (
                                ids.map((id) => {
                                    const a = assignments[id];
                                    const reason = assignmentInertReason(a);
                                    const span = a.targets.filter((t) =>
                                        t.startsWith("slide:")
                                    ).length;
                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            data-anchor={id}
                                            className="flex w-full cursor-pointer items-center gap-2 border-t border-text-primary/5 py-1.5 pl-12 pr-4 text-left"
                                            onClick={() => openAssignment(id)}
                                        >
                                            <Sparkles
                                                size={12}
                                                className="shrink-0 text-text-muted"
                                            />
                                            <span className="min-w-0 flex-1 truncate text-xs">
                                                {a.label}
                                            </span>
                                            <span className="shrink-0 text-[11px] text-text-muted">
                                                {reason === "skip"
                                                    ? "スキップ中"
                                                    : reason !== null
                                                      ? "実行されません"
                                                      : span > 1
                                                        ? `${span} 枚を担当`
                                                        : `${a.instruction.length.toLocaleString()} 字`}
                                            </span>
                                            <ChevronRight
                                                size={14}
                                                className="shrink-0 text-text-muted"
                                            />
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    );
                })}

                {/* どの枚にも属さない担当。一覧は枚を起点に組むので、ここが唯一の入口になる。
                    本文に対象を書かないまま離れた担当と、対象の図形が消えた担当が落ちてくる。 */}
                {!loading && unbound.length > 0 && (
                    <div className="mb-2.5 border-y border-text-primary/6 bg-surface">
                        <div className="flex items-center gap-2.5 border-b border-text-primary/5 bg-warning/10 px-4 py-2">
                            <TriangleAlert size={14} className="shrink-0 text-warning" />
                            <span className="min-w-0 flex-1 text-[13px] text-warning">
                                どの枚にも紐づいていない担当
                            </span>
                            <span className="shrink-0 text-[11px] text-warning">
                                {unbound.length}
                            </span>
                        </div>
                        {unbound.map((id) => {
                            const a = assignments[id];
                            const reason = assignmentInertReason(a);
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    data-anchor={id}
                                    className="flex w-full cursor-pointer items-center gap-2 border-t border-text-primary/5 py-1.5 pl-12 pr-4 text-left first:border-t-0"
                                    onClick={() => openAssignment(id)}
                                >
                                    <Sparkles size={12} className="shrink-0 text-text-muted" />
                                    <span className="min-w-0 flex-1 truncate text-xs">
                                        {a.label.trim().length > 0 ? a.label : "無題"}
                                    </span>
                                    <span className="shrink-0 text-[11px] text-text-muted">
                                        {reason === "skip" ? "スキップ中" : "実行されません"}
                                    </span>
                                    <ChevronRight size={14} className="shrink-0 text-text-muted" />
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
            <PaneFooter>
                <button
                    type="button"
                    className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded text-text-muted transition-colors hover:bg-text-primary/5 hover:text-interactive"
                    title="スライドを読み直す"
                    onClick={() => void refreshDeck()}
                >
                    <RefreshCw size={16} />
                </button>
                <span className="flex-1 text-[11px] text-warning">
                    {unset > 0 ? `ねらい未設定 ${unset}` : ""}
                </span>
                <button
                    type="button"
                    className="flex cursor-pointer items-center gap-1 rounded border border-border-subtle bg-surface px-3 py-1 text-xs font-medium"
                    onClick={() => openAssignment(createAssignmentId())}
                >
                    <Plus size={14} />
                    担当を足す
                </button>
            </PaneFooter>
        </>
    );
}
